import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { applyTimelineTime } from './AnimationSystem';
import { FrameReporter } from './FrameReporter';
import { TimelineController } from './TimelineController';

const FINAL_RANGE_HOLD_MS = 750;

export interface UseTimelineRuntimeOptions {
  controller: TimelineController;
  mixer: THREE.AnimationMixer | null;
  getMixer?: () => THREE.AnimationMixer | null;
  prepareForApply?: () => void;
  reporter: FrameReporter;
  enabled?: boolean;
  onRangeComplete?: () => void;
}

export function useTimelineRuntime({
  controller,
  mixer,
  getMixer,
  prepareForApply,
  reporter,
  enabled = true,
  onRangeComplete,
}: UseTimelineRuntimeOptions) {
  const lastAppliedTimeRef = useRef<number | null>(null);
  const completedRangeEndRef = useRef<number | null>(null);
  const finalRangeHoldTimeoutRef = useRef<number | null>(null);

  const clearPendingFinalHold = () => {
    if (finalRangeHoldTimeoutRef.current !== null) {
      window.clearTimeout(finalRangeHoldTimeoutRef.current);
      finalRangeHoldTimeoutRef.current = null;
    }
    completedRangeEndRef.current = null;
  };

  useEffect(() => {
    return () => {
      clearPendingFinalHold();
    };
  }, []);

  useFrame((_, delta) => {
    if (!enabled) return;
    const activeMixer = getMixer?.() ?? mixer;
    if (!activeMixer) return;

    const beforeUpdate = controller.getSnapshot();
    const isHoldingCompletedTerminalRange =
      completedRangeEndRef.current !== null &&
      !beforeUpdate.playing &&
      Math.abs(beforeUpdate.currentTime - completedRangeEndRef.current) < 1e-9 &&
      beforeUpdate.range.endTime !== null &&
      Math.abs(beforeUpdate.range.endTime - beforeUpdate.duration) < 1e-9;

    if (!isHoldingCompletedTerminalRange) {
      clearPendingFinalHold();
    }

    if (lastAppliedTimeRef.current === null || beforeUpdate.currentTime !== lastAppliedTimeRef.current) {
      prepareForApply?.();
      applyTimelineTime(activeMixer, beforeUpdate.currentTime);
      reporter.report(beforeUpdate.currentTime);
      lastAppliedTimeRef.current = beforeUpdate.currentTime;

      if (beforeUpdate.playing) {
        return;
      }
    }

    const didCompleteRange = controller.update(delta);
    const snapshot = controller.getSnapshot();
    const { currentTime, playing, range } = snapshot;

    prepareForApply?.();
    applyTimelineTime(activeMixer, currentTime);
    reporter.report(currentTime);
    lastAppliedTimeRef.current = currentTime;

    if (didCompleteRange) {
      completedRangeEndRef.current = currentTime;
      const isTerminalRange =
        range.endTime !== null && Math.abs(range.endTime - snapshot.duration) < 1e-9;

      if (isTerminalRange) {
        clearPendingFinalHold();
        completedRangeEndRef.current = currentTime;

        finalRangeHoldTimeoutRef.current = window.setTimeout(() => {
          finalRangeHoldTimeoutRef.current = null;
          completedRangeEndRef.current = null;
          onRangeComplete?.();
        }, FINAL_RANGE_HOLD_MS);
      } else {
        completedRangeEndRef.current = null;
        onRangeComplete?.();
      }
      return;
    }
  });
}
